import React from 'react';
import styled from 'styled-components';
import { colors } from '../../globalStyles';
import { MAX_INDEX_SCORE } from './constants';

type ScoreBarProps = {
  score: number | null | undefined;
  /** Accessible name for the bar, e.g. "Persistence Index". */
  label: string;
  showValue?: boolean;
};

export const ScoreBar = ({ score, label, showValue = false }: ScoreBarProps) => {
  // Per design: rows with no score leave the cell blank rather than showing an empty bar.
  if (score === null || score === undefined) return null;

  const displayValue = score.toFixed(1);
  const value = Number(displayValue);
  // The API types the score as an unbounded nullable Float, so clamp before it drives a CSS width.
  const fillPercent = Math.max(0, Math.min(100, (value / MAX_INDEX_SCORE) * 100));

  return (
    <ScoreBarRow>
      <Track
        role="meter"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={MAX_INDEX_SCORE}
      >
        <Fill $pct={fillPercent} />
      </Track>
      {showValue && <Value>{displayValue}</Value>}
    </ScoreBarRow>
  );
};

const ScoreBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
`;

const Track = styled.div`
  position: relative;
  flex: 1;
  height: 0.5rem;
  border-radius: 0.25rem;
  background-color: ${colors.grey.lightBorder};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => $pct}%;
  border-radius: 0.25rem;
  background-color: ${colors.secondary};
`;

const Value = styled.span`
  font-size: 0.8125rem;
  color: ${colors.text};
  min-width: 2.5rem;
  text-align: right;
`;
